/**
 * Client-side environment configuration for the Nosible monorepo
 * Provides type-safe environment variables and URL builders for browser code
 *
 * Note: Server-side code should use getServerEnv() from @nosible/api or package-specific env files
 */

const getPublicDomain = (): string => {
  // Bun's define replaces `import.meta.env.PUBLIC_DOMAIN` at build time
  const domain = import.meta.env.PUBLIC_DOMAIN;
  if (domain) return domain;
  throw new Error("PUBLIC_DOMAIN is required for client env composition");
};

const buildBaseUrls = (domain: string) => {
  const isProduction = domain.includes(".com");
  const protocol = isProduction ? "https" : "http";
  const port = isProduction ? "" : ":3000";
  const landingPort = isProduction ? "" : ":4000";

  const appBaseUrl = `${protocol}://app.${domain}${port}`;
  const adminBaseUrl = `${protocol}://admin.${domain}${port}`;

  // DEV-ONLY override: when Stuart's launcher runs the api on a dedicated port
  // (e.g. http://localhost:3010) instead of Patrick's hostname-routed :3000,
  // the SPAs must point their tRPC + better-auth client at that explicit URL.
  // PUBLIC_API_BASE_URL is inlined by admin/build.ts + account/build.ts when
  // set in the launcher environment; production builds never set it, so the
  // composed `api.{domain}` URL is used as before.
  const apiBaseOverride =
    typeof import.meta !== "undefined" && import.meta.env
      ? import.meta.env.PUBLIC_API_BASE_URL
      : undefined;
  const apiBaseUrl = apiBaseOverride
    ? String(apiBaseOverride).replace(/\/+$/, "")
    : `${protocol}://api.${domain}${port}`;

  return {
    API_BASE_URL: apiBaseUrl,
    APP_BASE_URL: appBaseUrl,
    ADMIN_BASE_URL: adminBaseUrl,
    LANDING_BASE_URL: `${protocol}://www.${domain}${landingPort}`,
    DOMAIN: domain,
    loginUrl: `${appBaseUrl}/login`,
    signUpUrl: `${appBaseUrl}/sign-up`,
  };
};

// Client-side environment (browser + SSR/build time)
export const getClientEnv = (domain?: string) => {
  const publicDomain = domain || getPublicDomain();
  return buildBaseUrls(publicDomain);
};

// DEV-ONLY: build-time flag that lets the SPA shells render with a stubbed
// session so local reviewers can see the reskinned UI without a real Better Auth
// session / Google login. Set PUBLIC_DEV_AUTH_BYPASS=1 in the dev launcher env;
// the value is injected via Bun's `define` (see admin/build.ts, account/build.ts).
// Never honoured in production builds — only consumers gating logic on this
// helper bypass auth, and the variable is opt-in.
export const isDevAuthBypass = (): boolean => {
  try {
    return import.meta.env.PUBLIC_DEV_AUTH_BYPASS === "1";
  } catch {
    return false;
  }
};

export type DevStubSession = {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    emailVerified: boolean;
    image: null;
    createdAt: Date;
    updatedAt: Date;
  };
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
    token: string;
    createdAt: Date;
    updatedAt: Date;
    ipAddress: string;
    userAgent: string;
  };
};

export const DEV_STUB_SESSION: DevStubSession = {
  user: {
    id: "user_lr6fgvtopqlh",
    email: "stuart@nosible.com",
    name: "Stuart Reid",
    role: "admin",
    emailVerified: true,
    image: null,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  },
  session: {
    id: "dev-stub-session",
    userId: "user_lr6fgvtopqlh",
    expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
    token: "dev-stub-token",
    createdAt: new Date(0),
    updatedAt: new Date(0),
    ipAddress: "127.0.0.1",
    userAgent: "dev-launcher",
  },
};
